import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  Grid, 
  CircularProgress,
  useTheme,
  useMediaQuery,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  Divider
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useParams } from 'react-router-dom';
import { employeeAPI, Employee } from '../services/api';
import projectService, { Project } from '../services/projectService';

// สถานะโครงการ
enum ProjectStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
}

const ProjectManagement: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState<boolean>(true);
  const [project, setProject] = useState<Project | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  
  // สถานะสำหรับ Dialog
  const [openStatusDialog, setOpenStatusDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [openAddDialog, setOpenAddDialog] = useState<boolean>(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [newStatus, setNewStatus] = useState<string>('');
  const [availableEmployees, setAvailableEmployees] = useState<Employee[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number>(0);
  
  // สถานะสำหรับ Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  
  useEffect(() => {
    if (id) {
      fetchProjectData();
    }
  }, [id]);
  
  const fetchProjectData = async () => {
    try {
      setLoading(true);
      
      // ดึงข้อมูลโครงการจาก API
      const projectData = await projectService.getProjectById(id as string);
      setProject(projectData);
      
      // จำลองการดึงข้อมูลพนักงานในโครงการ
      const data = await projectService.getProjectEmployees(parseInt(id as string));
      setEmployees(data.employees);
      
      // จำลองการดึงพนักงานที่ยังไม่ได้อยู่ในโครงการ
      const allEmployeesData = await employeeAPI.getEmployees();
      
      // กรองเฉพาะพนักงานที่ไม่ได้อยู่ในโครงการปัจจุบัน
      const availableEmps = allEmployeesData.employees.filter(emp => 
        !data.employees.some(e => e.id === emp.id)
      );
      
      setAvailableEmployees(availableEmps);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching project data:', error);
      setLoading(false);
    }
  };
  
  const handleBack = () => {
    navigate('/projects');
  };
  
  const handleOpenStatusDialog = (employee: Employee) => {
    setSelectedEmployee(employee);
    setNewStatus(employee.status);
    setOpenStatusDialog(true);
  };
  
  const handleCloseStatusDialog = () => {
    setOpenStatusDialog(false);
    setSelectedEmployee(null);
  };
  
  const handleOpenDeleteDialog = (employee: Employee) => {
    setSelectedEmployee(employee);
    setOpenDeleteDialog(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedEmployee(null);
  };
  
  const handleOpenAddDialog = () => {
    navigate(`/projects/${id}/employees/add`);
  };
  
  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewStatus(e.target.value);
  };
  
  const handleUpdateStatus = async () => {
    if (!selectedEmployee) return;
    
    try {
      setLoading(true);
      const success = await projectService.updateEmployeeWorkStatus(
        parseInt(id as string), 
        selectedEmployee.id, 
        newStatus
      );
      
      if (success) {
        // อัปเดตสถานะในรายการพนักงาน
        const updatedEmployees = employees.map(emp => 
          emp.id === selectedEmployee.id ? { ...emp, status: newStatus } : emp
        );
        
        setEmployees(updatedEmployees);
        
        setSnackbar({
          open: true,
          message: 'อัปเดตสถานะสำเร็จ',
          severity: 'success',
        });
      }
      
      handleCloseStatusDialog();
      setLoading(false);
    } catch (error) {
      console.error('Error updating employee status:', error);
      setSnackbar({
        open: true,
        message: 'เกิดข้อผิดพลาดในการอัปเดตสถานะ',
        severity: 'error',
      });
      setLoading(false);
    }
  };
  
  const handleDeleteEmployee = async () => {
    if (!selectedEmployee) return;
    
    try {
      setLoading(true);
      const success = await projectService.removeEmployeeFromProject(
        parseInt(id as string), 
        selectedEmployee.id
      );
      
      if (success) {
        // ลบพนักงานออกจากรายการ
        const updatedEmployees = employees.filter(emp => emp.id !== selectedEmployee.id);
        setEmployees(updatedEmployees);
        
        // เพิ่มพนักงานกลับไปยังรายการที่สามารถเพิ่มได้
        setAvailableEmployees([...availableEmployees, selectedEmployee]);
        
        setSnackbar({
          open: true,
          message: 'ลบพนักงานออกจากโครงการสำเร็จ',
          severity: 'success',
        });
      }
      
      handleCloseDeleteDialog();
      setLoading(false);
    } catch (error) {
      console.error('Error removing employee from project:', error);
      setSnackbar({
        open: true,
        message: 'เกิดข้อผิดพลาดในการลบพนักงาน',
        severity: 'error',
      });
      setLoading(false);
    }
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false,
    }));
  };
  
  const columns: GridColDef[] = [
    { 
      field: 'id', 
      headerName: 'ลำดับ', 
      width: 70,
      align: 'center', 
      headerAlign: 'center',
      minWidth: 50,
    },
    { 
      field: 'employeeCode', 
      headerName: 'รหัสพนักงาน', 
      width: isMobile ? 100 : 120,
      align: 'center', 
      headerAlign: 'center',
      minWidth: 100,
    },
    { 
      field: 'name', 
      headerName: 'ชื่อพนักงาน', 
      width: isMobile ? 200 : 250,
      flex: 1,
      minWidth: 150,
    },
    { 
      field: 'position', 
      headerName: 'ตำแหน่ง', 
      width: isMobile ? 150 : 180,
      align: 'center', 
      headerAlign: 'center',
      minWidth: 150,
      hideable: true,
      headerClassName: isMobile ? 'hidden-column' : '',
    },
    { 
      field: 'status', 
      headerName: 'สถานะ', 
      width: isMobile ? 120 : 150,
      align: 'center', 
      headerAlign: 'center',
      minWidth: 120,
      renderCell: (params) => {
        let color = 'default';
        if (params.value === 'กำลังทำงาน') color = 'success';
        else if (params.value === 'ลา') color = 'warning';
        else if (params.value === 'ขาดงาน') color = 'error';
        
        return (
          <Chip 
            label={params.value} 
            color={color as any}
            size={isMobile ? 'small' : 'medium'}
            sx={{ fontWeight: 500 }}
          />
        );
      }
    },
    {
      field: 'actions',
      headerName: 'จัดการ',
      width: isMobile ? 120 : 150,
      align: 'center',
      headerAlign: 'center',
      minWidth: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <EditIcon 
            onClick={() => handleOpenStatusDialog(params.row as Employee)}
            sx={{ 
              color: 'info.main', 
              cursor: 'pointer',
              fontSize: isMobile ? '1.2rem' : '1.5rem',
              '&:hover': { color: 'info.dark' }
            }} 
          />
          <DeleteIcon 
            onClick={() => handleOpenDeleteDialog(params.row as Employee)}
            sx={{ 
              color: 'error.main', 
              cursor: 'pointer',
              fontSize: isMobile ? '1.2rem' : '1.5rem',
              '&:hover': { color: 'error.dark' }
            }} 
          />
        </Box>
      ),
    },
  ];
  
  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1">
          จัดการพนักงานในโครงการ
        </Typography>
      </Box>
      
      {loading && !project ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      ) : project ? (
        <>
          <Paper sx={{ p: { xs: 2, sm: 3 }, width: '100%', mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  รหัสโครงการ:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {project.code || project.projectCode}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  ชื่อโครงการ:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {project.name || project.projectName}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  เวลาทำงาน:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {project.workingTime || `${project.workStartTime} - ${project.workEndTime}`}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  สถานที่:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {project.location || (project.latitude && project.longitude ? `${project.latitude}, ${project.longitude}` : 'ไม่ระบุ')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  สถานะ:
                </Typography>
                <Chip 
                  label={
                    project.status === ProjectStatus.NOT_STARTED ? 'ยังไม่เริ่ม' :
                    project.status === ProjectStatus.IN_PROGRESS ? 'กำลังดำเนินการ' :
                    project.status === ProjectStatus.COMPLETED ? 'เสร็จสิ้น' : project.status
                  } 
                  color={
                    project.status === ProjectStatus.IN_PROGRESS ? 'success' : 
                    project.status === ProjectStatus.COMPLETED ? 'info' : 
                    project.status === ProjectStatus.NOT_STARTED ? 'warning' : 'default'
                  }
                  sx={{ fontWeight: 500 }}
                />
              </Grid>
            </Grid>
          </Paper>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              รายชื่อพนักงานในโครงการ
            </Typography>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={handleOpenAddDialog}
            >
              เพิ่มพนักงาน
            </Button>
          </Box>
          
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <CircularProgress />
              </Box>
            ) : employees.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <Typography variant="h6" color="text.secondary">
                  ไม่พบข้อมูลพนักงานในโครงการนี้
                </Typography>
              </Box>
            ) : (
              <Box sx={{ 
                height: { xs: 400, sm: 500, md: 600 }, 
                width: '100%' 
              }}>
                <DataGrid
                  rows={employees}
                  columns={columns}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: isMobile ? 5 : 10,
                      },
                    },
                  }}
                  pageSizeOptions={isMobile ? [5] : [10, 25, 50]}
                  disableRowSelectionOnClick
                  sx={{
                    '& .MuiDataGrid-columnHeaders': {
                      bgcolor: 'background.default',
                      '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 'bold',
                        fontSize: isMobile ? '0.875rem' : '1rem',
                      },
                    },
                    '& .MuiDataGrid-cell': {
                      fontSize: isMobile ? '0.875rem' : '1rem',
                      px: isMobile ? 1 : 2,
                    },
                    '& .hidden-column': {
                      display: 'none',
                    },
                  }}
                />
              </Box>
            )}
          </Paper>
        </>
      ) : (
        <Typography variant="h6" color="error">
          ไม่พบข้อมูลโครงการ
        </Typography>
      )}
      
      {/* Dialog สำหรับแก้ไขสถานะพนักงาน */}
      <Dialog open={openStatusDialog} onClose={handleCloseStatusDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          แก้ไขสถานะพนักงาน
          <IconButton
            aria-label="close"
            onClick={handleCloseStatusDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedEmployee && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {selectedEmployee.name}
              </Typography>
              <TextField
                select
                fullWidth
                label="สถานะการทำงาน"
                value={newStatus}
                onChange={handleStatusChange}
                sx={{ mb: 2 }}
              >
                <MenuItem value="กำลังทำงาน">กำลังทำงาน</MenuItem>
                <MenuItem value="ลา">ลา</MenuItem>
                <MenuItem value="ขาดงาน">ขาดงาน</MenuItem>
              </TextField>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatusDialog}>ยกเลิก</Button>
          <Button 
            onClick={handleUpdateStatus} 
            variant="contained"
            disabled={loading}
          >
            {loading ? 'กำลังบันทึก...' : 'บันทึก'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog สำหรับลบพนักงานออกจากโครงการ */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          ลบพนักงานออกจากโครงการ
          <IconButton
            aria-label="close"
            onClick={handleCloseDeleteDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedEmployee && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                คุณต้องการลบพนักงาน <strong>{selectedEmployee.name}</strong> ออกจากโครงการนี้ใช่หรือไม่?
              </Typography>
              <Typography variant="body2" color="error">
                * การดำเนินการนี้ไม่สามารถเรียกคืนได้
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>ยกเลิก</Button>
          <Button 
            onClick={handleDeleteEmployee} 
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? 'กำลังลบ...' : 'ลบพนักงาน'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar สำหรับแสดงข้อความแจ้งเตือน */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectManagement; 